import PropTypes from 'prop-types';
import {useEffect, useCallback} from 'react';
import {useLocation} from 'react-router-dom';
import {paths} from 'packages/common/routes/paths';
import {useRouter} from 'packages/common/routes/hooks';
import {useAuthContext} from 'packages/common/hooks';
import {LoadingScreen} from 'packages/common/components/loading-screen';
import {RECENTLY_LOGGED_OUT_KEY, PRE_AUTH_PATH_KEY} from 'packages/common/utils/sessionStorageKeys';

/**
 * Garde d'authentification : redirige vers le login si non authentifié.
 *
 * @param {Object} props - Props du composant
 * @param {Object} props.children - Contenu protégé
 * @param {Object} [props.fallback] - Visuel affiché pendant la vérification
 *   (défaut : LoadingScreen ; le panel admin passe un SplashScreen pour la
 *   continuité avec le splash de session)
 * @return {JSX.Element} Le contenu ou le fallback
 */
export default function AuthGuard({children, fallback = <LoadingScreen />}) {
  const router = useRouter();
  const location = useLocation();
  const {isAuthenticated, loading} = useAuthContext();

  const check = useCallback(() => {
    if (!loading && !isAuthenticated) {
      if (!localStorage.getItem(RECENTLY_LOGGED_OUT_KEY)) {
        const fullPath = location.pathname + location.search;
        localStorage.setItem(PRE_AUTH_PATH_KEY, fullPath);
      } else {
        localStorage.removeItem(RECENTLY_LOGGED_OUT_KEY);
      }
      router.replace(paths.auth.login);
    }
  }, [isAuthenticated, loading, router, location.pathname, location.search]);

  useEffect(() => {
    check();
  }, [check]);

  if (loading) {
    return fallback;
  }

  return <>{children}</>;
}

AuthGuard.propTypes = {
  children: PropTypes.node,
  fallback: PropTypes.node,
};
